import { makeApi, Zodios, type ZodiosOptions } from "@zodios/core";
import { z } from "zod";

const postApiChat_Body = z.object({
  chatId: z.string().optional(),
  messages: z.array(
    z.object({
      role: z.string(),
      content: z.string().optional(),
      parts: z
        .array(
          z.union([
            z.string(),
            z.object({ type: z.string(), text: z.string().optional() }),
          ])
        )
        .optional(),
    })
  ),
  focusedId: z.string().optional(),
  interactible: z.string().optional(),
});
const postApiDataAgentsByAgentId_Body = z.object({
  chatId: z.string(),
  messages: z.array(
    z.object({
      role: z.string(),
      content: z.string().optional(),
      parts: z
        .array(
          z.union([
            z.string(),
            z.object({ type: z.string(), text: z.string().optional() }),
          ])
        )
        .optional(),
    })
  ),
  focusedId: z.string().optional(),
  interactible: z.string().optional(),
});

export const schemas = {
  postApiChat_Body,
  postApiDataAgentsByAgentId_Body,
};

const endpoints = makeApi([
  {
    method: "get",
    path: "/api/audit/events",
    alias: "getApiAuditEvents",
    description: `List all AI audit events`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().gte(1).lte(500).optional(),
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "reverse",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "phase",
        type: "Query",
        schema: z.enum(["start", "end"]).optional(),
      },
      {
        name: "eventKind",
        type: "Query",
        schema: z
          .enum([
            "workflow",
            "agent",
            "handoff",
            "tool",
            "generation",
            "guardrail",
            "response",
            "mcp_tools",
            "custom",
            "other",
          ])
          .optional(),
      },
      {
        name: "since",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "until",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "traceId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "groupId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "workflowName",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "agentName",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "actorId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "tenantId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.object({
      events: z.array(
        z.object({
          schemaVersion: z.literal(1),
          eventId: z.string(),
          recordedAt: z.string().datetime({ offset: true }),
          effectiveAt: z.string().datetime({ offset: true }),
          phase: z.enum(["start", "end"]),
          eventKind: z.enum([
            "workflow",
            "agent",
            "handoff",
            "tool",
            "generation",
            "guardrail",
            "response",
            "mcp_tools",
            "custom",
            "other",
          ]),
          traceId: z.string(),
          spanId: z.string().optional(),
          parentId: z.union([z.string(), z.null()]).optional(),
          workflowName: z.string().optional(),
          groupId: z.union([z.string(), z.null()]).optional(),
          tenantId: z.string().optional(),
          actorId: z.string().optional(),
          environment: z.string().optional(),
          agentName: z.string().optional(),
          fromAgent: z.string().optional(),
          toAgent: z.string().optional(),
          toolName: z.string().optional(),
          guardrailName: z.string().optional(),
          guardrailTriggered: z.boolean().optional(),
          error: z.unknown().optional(),
          payload: z.object({}).partial().passthrough(),
          integrity: z.object({
            algorithm: z.literal("SHA-256"),
            digest: z.string(),
          }),
        })
      ),
      cursor: z.union([z.string(), z.null()]),
      limit: z.union([z.number(), z.null()]),
      reverse: z.boolean(),
      since: z.union([z.string(), z.null()]).optional(),
      until: z.union([z.string(), z.null()]).optional(),
      phase: z.union([z.enum(["start", "end"]), z.null()]).optional(),
      eventKind: z
        .union([
          z.enum([
            "workflow",
            "agent",
            "handoff",
            "tool",
            "generation",
            "guardrail",
            "response",
            "mcp_tools",
            "custom",
            "other",
          ]),
          z.null(),
        ])
        .optional(),
      traceId: z.union([z.string(), z.null()]).optional(),
      groupId: z.union([z.string(), z.null()]).optional(),
      workflowName: z.union([z.string(), z.null()]).optional(),
      agentName: z.union([z.string(), z.null()]).optional(),
      actorId: z.union([z.string(), z.null()]).optional(),
      tenantId: z.union([z.string(), z.null()]).optional(),
    }),
  },
  {
    method: "get",
    path: "/api/audit/groups/:groupId",
    alias: "getApiAuditGroupsByGroupId",
    description: `List audit events for a group`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().gte(1).lte(500).optional(),
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "reverse",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "phase",
        type: "Query",
        schema: z.enum(["start", "end"]).optional(),
      },
      {
        name: "eventKind",
        type: "Query",
        schema: z
          .enum([
            "workflow",
            "agent",
            "handoff",
            "tool",
            "generation",
            "guardrail",
            "response",
            "mcp_tools",
            "custom",
            "other",
          ])
          .optional(),
      },
      {
        name: "since",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "until",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "groupId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({
      groupId: z.string(),
      events: z.array(
        z.object({
          schemaVersion: z.literal(1),
          eventId: z.string(),
          recordedAt: z.string().datetime({ offset: true }),
          effectiveAt: z.string().datetime({ offset: true }),
          phase: z.enum(["start", "end"]),
          eventKind: z.enum([
            "workflow",
            "agent",
            "handoff",
            "tool",
            "generation",
            "guardrail",
            "response",
            "mcp_tools",
            "custom",
            "other",
          ]),
          traceId: z.string(),
          spanId: z.string().optional(),
          parentId: z.union([z.string(), z.null()]).optional(),
          workflowName: z.string().optional(),
          groupId: z.union([z.string(), z.null()]).optional(),
          tenantId: z.string().optional(),
          actorId: z.string().optional(),
          environment: z.string().optional(),
          agentName: z.string().optional(),
          fromAgent: z.string().optional(),
          toAgent: z.string().optional(),
          toolName: z.string().optional(),
          guardrailName: z.string().optional(),
          guardrailTriggered: z.boolean().optional(),
          error: z.unknown().optional(),
          payload: z.object({}).partial().passthrough(),
          integrity: z.object({
            algorithm: z.literal("SHA-256"),
            digest: z.string(),
          }),
        })
      ),
      cursor: z.union([z.string(), z.null()]),
      limit: z.union([z.number(), z.null()]),
      reverse: z.boolean(),
      since: z.union([z.string(), z.null()]).optional(),
      until: z.union([z.string(), z.null()]).optional(),
      phase: z.union([z.enum(["start", "end"]), z.null()]).optional(),
      eventKind: z
        .union([
          z.enum([
            "workflow",
            "agent",
            "handoff",
            "tool",
            "generation",
            "guardrail",
            "response",
            "mcp_tools",
            "custom",
            "other",
          ]),
          z.null(),
        ])
        .optional(),
    }),
  },
  {
    method: "get",
    path: "/api/audit/handoffs",
    alias: "getApiAuditHandoffs",
    description: `List handoff audit events`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().gte(1).lte(500).optional(),
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "reverse",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "phase",
        type: "Query",
        schema: z.enum(["start", "end"]).optional(),
      },
      {
        name: "since",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "until",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
    ],
    response: z.object({
      events: z.array(
        z.object({
          schemaVersion: z.literal(1),
          eventId: z.string(),
          recordedAt: z.string().datetime({ offset: true }),
          effectiveAt: z.string().datetime({ offset: true }),
          phase: z.enum(["start", "end"]),
          eventKind: z.enum([
            "workflow",
            "agent",
            "handoff",
            "tool",
            "generation",
            "guardrail",
            "response",
            "mcp_tools",
            "custom",
            "other",
          ]),
          traceId: z.string(),
          spanId: z.string().optional(),
          parentId: z.union([z.string(), z.null()]).optional(),
          workflowName: z.string().optional(),
          groupId: z.union([z.string(), z.null()]).optional(),
          tenantId: z.string().optional(),
          actorId: z.string().optional(),
          environment: z.string().optional(),
          agentName: z.string().optional(),
          fromAgent: z.string().optional(),
          toAgent: z.string().optional(),
          toolName: z.string().optional(),
          guardrailName: z.string().optional(),
          guardrailTriggered: z.boolean().optional(),
          error: z.unknown().optional(),
          payload: z.object({}).partial().passthrough(),
          integrity: z.object({
            algorithm: z.literal("SHA-256"),
            digest: z.string(),
          }),
        })
      ),
      cursor: z.union([z.string(), z.null()]),
      limit: z.union([z.number(), z.null()]),
      reverse: z.boolean(),
      since: z.union([z.string(), z.null()]).optional(),
      until: z.union([z.string(), z.null()]).optional(),
      phase: z.union([z.enum(["start", "end"]), z.null()]).optional(),
      eventKind: z
        .union([
          z.enum([
            "workflow",
            "agent",
            "handoff",
            "tool",
            "generation",
            "guardrail",
            "response",
            "mcp_tools",
            "custom",
            "other",
          ]),
          z.null(),
        ])
        .optional(),
    }),
  },
  {
    method: "get",
    path: "/api/audit/traces/:traceId",
    alias: "getApiAuditTracesByTraceId",
    description: `List audit events for a trace`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().int().gte(1).lte(500).optional(),
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "reverse",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "phase",
        type: "Query",
        schema: z.enum(["start", "end"]).optional(),
      },
      {
        name: "eventKind",
        type: "Query",
        schema: z
          .enum([
            "workflow",
            "agent",
            "handoff",
            "tool",
            "generation",
            "guardrail",
            "response",
            "mcp_tools",
            "custom",
            "other",
          ])
          .optional(),
      },
      {
        name: "since",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "until",
        type: "Query",
        schema: z.string().datetime({ offset: true }).optional(),
      },
      {
        name: "traceId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({
      traceId: z.string(),
      events: z.array(
        z.object({
          schemaVersion: z.literal(1),
          eventId: z.string(),
          recordedAt: z.string().datetime({ offset: true }),
          effectiveAt: z.string().datetime({ offset: true }),
          phase: z.enum(["start", "end"]),
          eventKind: z.enum([
            "workflow",
            "agent",
            "handoff",
            "tool",
            "generation",
            "guardrail",
            "response",
            "mcp_tools",
            "custom",
            "other",
          ]),
          traceId: z.string(),
          spanId: z.string().optional(),
          parentId: z.union([z.string(), z.null()]).optional(),
          workflowName: z.string().optional(),
          groupId: z.union([z.string(), z.null()]).optional(),
          tenantId: z.string().optional(),
          actorId: z.string().optional(),
          environment: z.string().optional(),
          agentName: z.string().optional(),
          fromAgent: z.string().optional(),
          toAgent: z.string().optional(),
          toolName: z.string().optional(),
          guardrailName: z.string().optional(),
          guardrailTriggered: z.boolean().optional(),
          error: z.unknown().optional(),
          payload: z.object({}).partial().passthrough(),
          integrity: z.object({
            algorithm: z.literal("SHA-256"),
            digest: z.string(),
          }),
        })
      ),
      cursor: z.union([z.string(), z.null()]),
      limit: z.union([z.number(), z.null()]),
      reverse: z.boolean(),
      since: z.union([z.string(), z.null()]).optional(),
      until: z.union([z.string(), z.null()]).optional(),
      phase: z.union([z.enum(["start", "end"]), z.null()]).optional(),
      eventKind: z
        .union([
          z.enum([
            "workflow",
            "agent",
            "handoff",
            "tool",
            "generation",
            "guardrail",
            "response",
            "mcp_tools",
            "custom",
            "other",
          ]),
          z.null(),
        ])
        .optional(),
    }),
  },
  {
    method: "post",
    path: "/api/chat",
    alias: "postApiChat",
    description: `Chat with the AI agent`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: postApiChat_Body,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/data-agents",
    alias: "getApiDataAgents",
    description: `List all available data agents with optional pagination and search`,
    requestFormat: "json",
    parameters: [
      {
        name: "page",
        type: "Query",
        schema: z
          .number()
          .int()
          .gte(1)
          .lte(9007199254740991)
          .optional()
          .default(1),
      },
      {
        name: "pageSize",
        type: "Query",
        schema: z.number().int().gte(1).lte(100).optional().default(10),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.object({
      agents: z.array(
        z.object({ id: z.string(), name: z.string(), description: z.string() })
      ),
      pagination: z.object({
        page: z.number(),
        pageSize: z.number(),
        total: z.number(),
        totalPages: z.number(),
      }),
    }),
  },
  {
    method: "post",
    path: "/api/data-agents/:agent_id",
    alias: "postApiDataAgentsByAgentId",
    description: `Chat with a specific data agent`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: postApiDataAgentsByAgentId_Body,
      },
      {
        name: "agent_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/data-agents/chats",
    alias: "getApiDataAgentsChats",
    description: `List saved chats`,
    requestFormat: "json",
    response: z.object({
      chats: z.array(
        z.object({
          id: z.string(),
          title: z.string(),
          createdAt: z.number(),
          updatedAt: z.number(),
          lastMessage: z.string().optional(),
          agentId: z.string().optional(),
        })
      ),
      cursor: z.string().optional(),
    }),
  },
  {
    method: "get",
    path: "/api/data-agents/history/:chatId",
    alias: "getApiDataAgentsHistoryByChatId",
    description: `Get chat history`,
    requestFormat: "json",
    parameters: [
      {
        name: "chatId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.array(
      z.object({
        role: z.string(),
        content: z.string(),
        timestamp: z.number(),
        metadata: z.object({}).partial().passthrough().optional(),
      })
    ),
  },
  {
    method: "post",
    path: "/api/data-agents/route",
    alias: "postApiDataAgentsRoute",
    description: `Route a user message to the most appropriate data agent`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ message: z.string() }),
      },
    ],
    response: z.object({
      agentId: z.string(),
      agentName: z.string(),
      reasoning: z.string().optional(),
    }),
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
